/**
 * Content Service
 *
 * Centralized service for fetching all site content. Currently resolves
 * from local data files, but every method returns a Promise so it can
 * be swapped to a real REST / GraphQL API with zero component changes.
 *
 * Usage:
 *   import { contentService } from '../services';
 *
 *   // Inside a component or useEffect
 *   const banner = await contentService.getHomeBanner();
 *   const menu   = await contentService.getMenuItems();
 *
 * Or use the provided React hook for cleaner integration:
 *   import { useContent } from '../services';
 *
 *   function MyComponent() {
 *     const { data, loading, error } = useContent('homeBanner');
 *     ...
 *   }
 */

import { API_CONFIG, apiFetch } from './api';
import {
  siteContent,
  homeContent,
  menuContent,
  teamContent,
  aboutContent,
  contactContent,
  reservationContent,
} from './content';

// ────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────

/**
 * Simulate async API delay (removed in production / real API mode).
 * Keeps the interface identical so components don't need to change.
 */
const resolveData = (data) => {
  if (API_CONFIG.USE_MOCK_DATA) {
    return Promise.resolve(data);
  }
  // When USE_MOCK_DATA is false, this path is never reached;
  // each method will call apiFetch instead.
  return Promise.resolve(data);
};

// ────────────────────────────────────────────────────
// Content Service
// ────────────────────────────────────────────────────

const contentService = {
  // ───── Site-wide ─────────────────────────────────

  /** Brand info (name, logos, tagline) */
  getBrandInfo: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(siteContent.brand)
      : apiFetch('/api/site/brand'),

  /** Business info (address, phone, email, hours) */
  getBusinessInfo: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(siteContent.businessInfo)
      : apiFetch('/api/site/business-info'),

  /** Social media links */
  getSocialLinks: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(siteContent.socialLinks)
      : apiFetch('/api/site/social-links'),

  /** Navigation config (main nav, sidebar, CTA) */
  getNavigation: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(siteContent.navigation)
      : apiFetch('/api/site/navigation'),

  /** Footer content */
  getFooterContent: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(siteContent.footer)
      : apiFetch('/api/site/footer'),

  /** All site-wide content in one call */
  getSiteContent: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(siteContent)
      : apiFetch('/api/site'),

  // ───── Home Page ─────────────────────────────────

  /** Hero banner slides */
  getHomeBanner: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(homeContent.banner)
      : apiFetch('/api/home/banner'),

  /** Feature cards */
  getHomeFeatures: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(homeContent.features)
      : apiFetch('/api/home/features'),

  /** Our Story section */
  getHomeStory: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(homeContent.story)
      : apiFetch('/api/home/story'),

  /** Offers section */
  getHomeOffers: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(homeContent.offers)
      : apiFetch('/api/home/offers'),

  /** Special Dish promo */
  getHomeSpecialDish: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(homeContent.specialDish)
      : apiFetch('/api/home/special-dish'),

  /** Live Sports & Big Screen */
  getHomeLiveSports: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(homeContent.liveSports)
      : apiFetch('/api/home/live-sports'),

  /** Best Specialties carousel */
  getHomeSpecials: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(homeContent.specials)
      : apiFetch('/api/home/specials'),

  /** Why Choose Us */
  getHomeWhyUs: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(homeContent.whyUs)
      : apiFetch('/api/home/why-us'),

  /** Video section */
  getHomeVideoSection: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(homeContent.videoSection)
      : apiFetch('/api/home/video'),

  /** Testimonials */
  getHomeTestimonials: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(homeContent.testimonials)
      : apiFetch('/api/home/testimonials'),

  /** Reservation form config */
  getHomeReservation: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(homeContent.reservation)
      : apiFetch('/api/home/reservation'),

  /** Recent Updates / Events */
  getHomeRecentUpdates: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(homeContent.recentUpdates)
      : apiFetch('/api/home/recent-updates'),

  /** All home page content in one call */
  getHomeContent: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(homeContent)
      : apiFetch('/api/home'),

  // ───── Menu ──────────────────────────────────────

  /** Tabbed menu section (Delicious Menu) */
  getDeliciousMenu: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(menuContent.deliciousMenu)
      : apiFetch('/api/menu/delicious'),

  /** All menu content */
  getMenuContent: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(menuContent)
      : apiFetch('/api/menu'),

  // ───── Team / Chefs ──────────────────────────────

  /** Team section header */
  getTeamSection: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(teamContent.section)
      : apiFetch('/api/team/section'),

  /** Chef profiles */
  getChefs: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(teamContent.chefs)
      : apiFetch('/api/team/chefs'),

  /** All team content */
  getTeamContent: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(teamContent)
      : apiFetch('/api/team'),

  // ───── About ─────────────────────────────────────

  /** About page content */
  getAboutContent: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(aboutContent)
      : apiFetch('/api/about'),

  // ───── Contact ───────────────────────────────────

  /** Contact page content */
  getContactContent: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(contactContent)
      : apiFetch('/api/contact'),

  /** Location list */
  getLocations: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(contactContent.locations)
      : apiFetch('/api/contact/locations'),

  // ───── Reservation ───────────────────────────────

  /** Reservation page content */
  getReservationContent: () =>
    API_CONFIG.USE_MOCK_DATA
      ? resolveData(reservationContent)
      : apiFetch('/api/reservation'),
};

export default contentService;
